import { useEffect, useState } from 'react';
import Icon from './Icon';
import { useAuth } from '../context/AuthContext';
import { subscribeTenants } from '../data/listeners/tenants';
import { useIsMobile } from '../hooks/useIsMobile';
import { BRAND } from '../config/brands';
import ChangePasswordModal from './ChangePasswordModal';

const ROLE_LABEL = { hqadmin: 'HQ Admin', owner: 'Owner', manager: 'Manager', agent: 'Agent' };

// Seeded demo accounts (scripts/seed.mjs). The "Demo role" switch re-signs-in as
// the matching user so the UI scope always equals what the server rules allow.
const DEMO_USER = {
  hqadmin: 'admin@presto.ph',
  owner: 'owner@presto.ph',
  manager: 'manager@presto.ph',
  agent: 'bea@presto.ph',
};
const DEMO_PASSWORD = 'Presto-2026!';

// BUG-026: the demo-role switcher is a testing aid, not a real-user control. Show
// it only in dev (or when explicitly opted in) — in a prod build import.meta.env.DEV
// is statically false, so Vite drops the gated blocks from the bundle entirely.
const SHOW_DEMO_ROLE = import.meta.env.DEV || import.meta.env.VITE_SHOW_DEMO_ROLE === 'true';

// Tenant options the switcher offers. HQ admins act WITHIN a chosen tenant (so
// they can create one then switch in to set it up) — the live tenants list feeds
// the switcher. Owners switch among the tenants they own (their tenantIds).
function ownerOptions(homeTenantId, tenantIds) {
  const ids = tenantIds.length ? tenantIds : [homeTenantId];
  return ids.filter(Boolean).map((t) => ({ value: t, label: t }));
}

export default function Topbar({ onLogVisit }) {
  const {
    profile, role, homeTenantId, tenantIds, activeTenantId,
    setActiveTenant, signIn, signOutUser,
  } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const isMobile = useIsMobile();

  // HQ admins get the live list of every tenant (rules allow the full read);
  // owners don't subscribe (they switch among their own tenantIds).
  useEffect(() => {
    if (role !== 'hqadmin') { setTenants([]); return undefined; }
    return subscribeTenants(setTenants, () => setTenants([]));
  }, [role]);

  let opts = [];
  if (role === 'hqadmin') {
    opts = tenants.map((t) => ({ value: t.id, label: t.name ?? t.id }));
    if (activeTenantId && !opts.some((o) => o.value === activeTenantId)) {
      opts = [{ value: activeTenantId, label: activeTenantId }, ...opts]; // before the list lands
    }
  } else if (role === 'owner') {
    opts = ownerOptions(homeTenantId, tenantIds);
  }
  const showTenant = opts.length > 0;
  const displayName = profile?.name ?? profile?.email ?? '—';
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '–';

  const switchRole = async (nextRole) => {
    const email = DEMO_USER[nextRole];
    if (!email) return;
    setSwitching(true);
    try {
      await signIn(email, DEMO_PASSWORD); // onAuthStateChanged re-reads role/tenant/scope
    } catch (e) {
      console.error('demo role switch failed', e);
    } finally {
      setSwitching(false);
    }
  };

  if (isMobile) {
    return (
      <div className="topbar">
        <span className="topbar-brand">
          <img
            src={BRAND.logoSrc}
            alt={BRAND.name}
            style={{ height: BRAND.topbarLogoH || 22 }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <span className="tag">{BRAND.tag}</span>
        </span>
        <span className="spacer" />
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Account menu"
        >
          <span className="avatar">{initials}</span>
        </button>
        {menuOpen && (
          <>
            <div className="scrim" onClick={() => setMenuOpen(false)} />
            <div className="more-sheet" role="menu" aria-label="Account">
              <div style={{ padding: '4px 11px 8px', fontSize: 'var(--text-small)' }}>
                <b>{displayName}</b>{role && <span className="role"> · {ROLE_LABEL[role]}</span>}
              </div>
              {showTenant && (
                <label className="more-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                  Active tenant
                  <select aria-label="Active tenant" value={activeTenantId ?? ''} onChange={(e) => setActiveTenant(e.target.value)}>
                    {opts.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
              )}
              {SHOW_DEMO_ROLE && (
                <label className="more-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                  Demo role
                  <select aria-label="Demo role" value={role ?? 'agent'} disabled={switching} onChange={(e) => switchRole(e.target.value)}>
                    <option value="hqadmin">HQ Admin</option>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="agent">Agent</option>
                  </select>
                </label>
              )}
              <button className="more-item" onClick={() => { setMenuOpen(false); setShowChangePw(true); }}>Change password</button>
              <button className="more-item" onClick={signOutUser}>Sign out</button>
            </div>
          </>
        )}
        {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
      </div>
    );
  }

  return (
    <div className="topbar">
      <span className="spacer" />
      <button className="btn btn-primary btn-sm" onClick={onLogVisit}>
        <Icon name="logvisit" size={15} /> Log Touchpoint
      </button>

      {/* Right cluster: tenant + demo-role + identity + sign-out, grouped. */}
      <span className="topbar-divider hide-sm" />
      {showTenant && (
        <select
          className="hide-sm"
          aria-label="Active tenant"
          value={activeTenantId ?? ''}
          onChange={(e) => setActiveTenant(e.target.value)}
        >
          {opts.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
      {SHOW_DEMO_ROLE && (
        <span className="pill hide-sm">
          Demo role
          <select
            aria-label="Demo role"
            value={role ?? 'agent'}
            disabled={switching}
            onChange={(e) => switchRole(e.target.value)}
            style={{ height: 26, border: 'none', background: 'rgba(34,34,39,.08)', color: 'var(--presto-suit)', fontWeight: 600, borderRadius: 'var(--radius-sm)' }}
          >
            <option value="hqadmin">HQ Admin</option>
            <option value="owner">Owner</option>
            <option value="manager">Manager</option>
            <option value="agent">Agent</option>
          </select>
        </span>
      )}
      <span className="user-chip hide-sm" title={displayName}>
        <span className="avatar">{initials}</span>
        <span className="who">
          <b>{displayName}</b>
          {role && (
            <span className="role" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '12px', color: 'var(--muted)', marginTop: 2 }}>
              <span>👑</span>
              <span>{ROLE_LABEL[role]}</span>
            </span>
          )}
        </span>
      </span>
      <a className="hide-sm" onClick={() => setShowChangePw(true)} style={{ cursor: 'pointer', fontSize: '13px', color: 'var(--muted)', textDecoration: 'none', borderRadius: 'var(--radius-sm)', padding: '4px 8px', transition: 'background .15s' }} onMouseEnter={(e) => e.target.style.background = 'rgba(34,34,39,.06)'} onMouseLeave={(e) => e.target.style.background = 'none'}>Change password</a>
      <button className="btn btn-ghost btn-sm hide-sm" onClick={signOutUser} style={{ fontSize: '16px', lineHeight: 1, padding: 0, minWidth: 'auto', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>→</button>
      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
